import {
  AnchorButton,
  ButtonGroup,
  Intent,
  Menu,
  MenuItem,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { emptyObject } from 'vet-bones/constants';
import { usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { FileExtension } from 'src/constants/fileExtensions';
import { TEST_IDS } from 'src/constants/testIds';
import { Project } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { duplicateProjectToCloud } from 'src/utils/duplicate/duplicateProjectToCloud';
import { decodeLocalContent } from 'src/utils/exportProject/encodeProject';
import { generateDateTimeId } from 'src/utils/generateId';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useNavigateProject } from 'src/utils/hooks/useNavigateProject';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { readFile } from 'src/utils/readFile';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

type newProjectButtonProps = {
  isImportAllowed: boolean;
};

export const NewProjectButton: React.VFC<newProjectButtonProps> = ({
  isImportAllowed,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const navigateProject = useNavigateProject();
  const defaultConnection = useDefaultPortalConnection();

  const portalUser = usePortalUser();
  const isCloudStorageEnabled = portalUser.data?.is_designer_storage_enabled;

  const onDropAccepted = async (files: File[]) => {
    try {
      const [file] = files;
      const contents = await readFile(file);

      const data = decodeLocalContent(contents);
      const project = Object.values(
        data?.projects?.projectsById || emptyObject
      )[0] as Project | undefined;

      if (!project) {
        return;
      }

      const newProject = duplicateProject(project, {
        dateCreated: Date.now(),
        id: generateDateTimeId(),
        connectionId: defaultConnection?.id,
        // do not copy voicebox related data
        voiceboxConversationId: undefined,
        voiceboxMessageHistory: undefined,
        voiceboxEvaluatedCompetencyQuestions: undefined,
        voiceboxEvaluatedSummary: undefined,
      });

      if (isCloudStorageEnabled) {
        // Import to cloud
        const newProjectId = await duplicateProjectToCloud(newProject);
        if (newProjectId && isMounted.current) {
          // Wait for cloud projects to be refetched
          navigate(getProjectRoute(newProjectId));
        }
      } else {
        // Import as local project
        dispatch(projectsActionCreators.importedProjectFromFile(newProject));
        navigateProject(newProject.id);
      }
    } catch {
      dispatch(projectsActionCreators.importedInvalidProjectFromFile());
    }
  };

  const { getInputProps, open: openImport } = useDropzone({
    accept: [FileExtension.STARDOG_DESIGNER],
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted,
  });

  const handleCreateProject = () => {
    dispatch(
      uiSlice.actions.toggledCreateProjectOverlay({
        isOpen: true,
      })
    );
  };

  return (
    <ButtonGroup className="sd-button-group-data">
      <AnchorButton
        data-testid={TEST_IDS.home.createProjectButton}
        icon={isImportAllowed ? IconNames.PLUS : null}
        intent={Intent.PRIMARY}
        onClick={handleCreateProject}
        text={
          isImportAllowed
            ? copy.components.home.new
            : copy.components.home.newProject
        }
      />

      {isImportAllowed ? (
        <>
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...getInputProps()}
            data-testid={TEST_IDS.home.importProject.fileInput}
          />

          <Popover2
            content={
              <Menu>
                <MenuItem
                  data-testid={TEST_IDS.home.importProject.button}
                  onClick={openImport}
                  text={copy.components.home.importProject}
                />
              </Menu>
            }
            position={Position.BOTTOM_LEFT}
          >
            <AnchorButton
              data-testid={TEST_IDS.home.createMenuButton}
              icon={IconNames.CARET_DOWN}
              intent={Intent.PRIMARY}
            />
          </Popover2>
        </>
      ) : null}
    </ButtonGroup>
  );
};
